/**
 * Operation Preview Panel
 *
 * Shows a preview of the operation and provides Execute/Cancel buttons.
 */

import React from 'react';
import { Text, Button, Spinner } from '@fluentui/react-components';
import { CheckmarkRegular, DismissRegular } from '@fluentui/react-icons';
import type { ExcelOperationPlan } from '@excel-ai-studio/shared';

interface OperationPreviewPanelProps {
  plan: ExcelOperationPlan;
  onExecute: () => void;
  onCancel: () => void;
  loading: boolean;
}

export const OperationPreviewPanel: React.FC<OperationPreviewPanelProps> = ({
  plan,
  onExecute,
  onCancel,
  loading,
}) => {
  return (
    <div className="card">
      <Text className="section-title">Aperçu de l'opération</Text>

      <div style={{ marginBottom: 16, padding: 12, backgroundColor: 'var(--colorNeutralBackground3)', borderRadius: 4 }}>
        <Text size={200} style={{ display: 'block', marginBottom: 8 }}>
          <strong>Intention détectée:</strong> {plan.intent}
        </Text>
        <Text size={200} style={{ display: 'block', marginBottom: 8 }}>
          <strong>Action:</strong> {plan.description}
        </Text>

        {plan.targetRange && (
          <Text size={200} style={{ display: 'block', marginBottom: 8 }}>
            <strong>Plage:</strong> <code>{plan.targetRange}</code>
          </Text>
        )}

        {plan.targetColumns && plan.targetColumns.length > 0 && (
          <Text size={200} style={{ display: 'block', marginBottom: 8 }}>
            <strong>Colonnes:</strong> {plan.targetColumns.join(', ')}
          </Text>
        )}

        {plan.parameters && Object.keys(plan.parameters).length > 0 && (
          <div>
            <Text size={200} weight="semibold" style={{ display: 'block', marginBottom: 4 }}>
              Paramètres:
            </Text>
            {Object.entries(plan.parameters).map(([key, value]) => (
              <Text key={key} size={200} style={{ display: 'block', marginLeft: 8 }}>
                • {key}: {String(value)}
              </Text>
            ))}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <Button
          icon={<CheckmarkRegular />}
          onClick={onExecute}
          disabled={loading}
          appearance="primary"
        >
          {loading ? <Spinner size="tiny" /> : 'Appliquer'}
        </Button>
        <Button
          icon={<DismissRegular />}
          onClick={onCancel}
          disabled={loading}
          appearance="secondary"
        >
          Annuler
        </Button>
      </div>
    </div>
  );
};
